"use client";

import { Box, type SxProps, type Theme } from "@mui/material";
import { LinkBubbleMenu, RichTextEditor, TableBubbleMenu } from "mui-tiptap";
import useExtensions from "./useExtensions";

export const richContentProseSx: SxProps<Theme> = {
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    marginTop: 2,
    marginBottom: 1,
  },
  "& p": {
    marginBottom: 1,
  },
  "& ul, & ol": {
    marginLeft: 3,
    marginBottom: 1,
  },
  "& img": {
    maxWidth: "100%",
    height: "auto",
    "&.resizable-image": {
      cursor: "default",
    },
  },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 1,
    "& th, & td": {
      border: "1px solid",
      borderColor: "divider",
      padding: 1,
    },
  },
  "& blockquote": {
    borderLeft: "4px solid",
    borderColor: "divider",
    paddingLeft: 2,
    marginLeft: 0,
    marginRight: 0,
    fontStyle: "italic",
  },
  "& pre": {
    backgroundColor: "grey.100",
    padding: 2,
    borderRadius: 1,
    overflow: "auto",
  },
  "& code": {
    backgroundColor: "grey.100",
    padding: "0.2em 0.4em",
    borderRadius: 1,
    fontSize: "0.9em",
  },
  '& div[data-type="iframe"], & .iframe-container': {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%",
    marginBottom: 2,
    "& iframe": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      border: "none",
    },
  },
  "& a": {
    color: "primary.main",
    textDecoration: "underline",
  },
};

type RichContentRendererProps = {
  content?: string;
  sx?: SxProps<Theme>;
};

const RichContentRenderer = ({ content = "", sx }: RichContentRendererProps) => {
  const extensions = useExtensions({ placeholder: "" });

  if (!content?.trim()) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "100%",
        "& .ProseMirror": {
          outline: "none",
          ...richContentProseSx,
        },
        ...sx,
      }}
    >
      <RichTextEditor
        immediatelyRender={false}
        extensions={extensions}
        content={content}
        editable={false}
        RichTextFieldProps={{
          variant: "standard",
          MenuBarProps: { hide: true },
        }}
      >
        {() => (
          <>
            <LinkBubbleMenu />
            <TableBubbleMenu />
          </>
        )}
      </RichTextEditor>
    </Box>
  );
};

export default RichContentRenderer;
